import React from "react";
import PhoneInputModule from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { autoFormatGbPhone, stripTrunkZero } from "./phoneNormalise";

const PhoneInput = PhoneInputModule.default ?? PhoneInputModule;

/**
 * Phone input with country dial code selector.
 *
 * Users in the UK (and other countries that use a "0" national trunk prefix)
 * often type their number with the leading 0, e.g. "07402155356". In the
 * international format the trunk 0 must be dropped, otherwise the default UK
 * mask truncates the last digit. To handle this without losing digits:
 *
 * - During typing, onChange strips a leading 0 placed directly after the dial
 *   code, so "+44 0 7402 155356" is stored as "+447402155356".
 * - On paste, onPaste strips any leading zeros from the clipboard text before
 *   the library processes it, so the mask has room for all 10 subscriber digits.
 *
 * The dial code itself is locked via countryCodeEditable=false; country is
 * changed only via the dropdown.
 *
 * Legacy/malformed values that carry no valid country code (e.g. a national
 * number stored with a bare "+", "+07939343002") would otherwise load frozen
 * because the value never matches the locked dial code. On open we auto-format
 * such a value to editable international form (anchored to UK, +44), write it
 * back so a save persists the correction, and show a notice below the field so
 * the agent can verify the country/number and change it via the dropdown.
 *
 * @param {object} props
 * @param {(data: {e: string, index: number, field: string}) => void} props.handleCountryPhone - Change handler used in on_page mode
 * @param {string} props.countryPhoneValue - Current phone value
 * @param {object} props.formik - Formik bag used outside on_page mode
 * @param {string} props.fieldKey - Formik field key for the phone value
 * @param {string} props.type - When "on_page", routes changes via handleCountryPhone instead of formik
 * @param {number} props.functionData - Array index for on_page mode
 * @returns {JSX.Element}
 */
const CountryPhoneNumber = ({
  handleCountryPhone,
  countryPhoneValue,
  formik,
  fieldKey,
  type,
  functionData,
}) => {
  const { value: displayValue, formatted: autoFormatted } =
    autoFormatGbPhone(countryPhoneValue);
  const [showNotice, setShowNotice] = React.useState(false);

  /**
   * When a malformed value is auto-formatted, write the corrected value back so
   * the field is editable and an unedited save still persists the proper value,
   * and flag the notice. The write is idempotent — once stored, the value is no
   * longer malformed, so this does not loop.
   */
  React.useEffect(() => {
    if (!autoFormatted) return;
    setShowNotice(true);
    // Persist without the leading "+", matching what react-phone-input-2's
    // onChange (see handleChange) stores on a manual edit, so the saved
    // representation is the same whether or not the agent touches the field.
    const normalisedValue = displayValue.replace(/^\+/, "");
    if (type === "on_page") {
      handleCountryPhone({
        e: normalisedValue,
        index: functionData,
        field: fieldKey,
      });
    } else {
      formik.setFieldValue(fieldKey, normalisedValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryPhoneValue, autoFormatted]);

  /** Handle a phone number change, delegating trunk-zero stripping to stripTrunkZero. */
  const handleChange = (value, data) => {
    const nextValue = stripTrunkZero(value, data?.dialCode, data?.countryCode);

    if (type === "on_page") {
      handleCountryPhone({
        e: nextValue,
        index: functionData,
        field: fieldKey,
      });
      return;
    }
    formik.setFieldValue(fieldKey, nextValue);
  };

  const handlePaste = (event) => {
    const clipboard = event.clipboardData || window.clipboardData;
    if (!clipboard) return;
    const pasted = clipboard.getData("text") || "";
    const digits = pasted.replace(/\D/g, "");
    if (digits.startsWith("0")) {
      event.preventDefault();
      const normalized = digits.replace(/^0+/, "");
      const input = event.target;
      const start = input.selectionStart ?? input.value.length;
      const end = input.selectionEnd ?? input.value.length;
      const before = input.value.slice(0, start);
      const after = input.value.slice(end);
      const descriptor = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        "value"
      );
      const nativeSetter = descriptor && descriptor.set;
      if (nativeSetter) {
        nativeSetter.call(input, before + normalized + after);
        input.dispatchEvent(new Event("input", { bubbles: true }));
      }
    }
  };

  return (
    <>
      <PhoneInput
        inputClassName="form-control "
        country={"gb"}
        inputProps={{
          style: { width: "100%" },
          onPaste: handlePaste,
        }}
        placeholder="Enter phone number"
        value={displayValue}
        countryCodeEditable={false}
        onChange={handleChange}
      />
      {showNotice ? (
        <small className="text-muted d-block mt-1">
          Auto-formatted to international format — please verify the country and
          number.
        </small>
      ) : null}
    </>
  );
};

export default CountryPhoneNumber;
